<template>
	<div class="container py-4">
		<!-- Header -->
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/configuration/integration"
				title="Back to integrations"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div class="flex-grow-1">
				<h1 class="mb-0">FieldSeeker GIS</h1>
				<p class="text-muted mb-0">
					Configure your Frontier Precision FieldSeeker GIS connection
				</p>
			</div>
			<button
				class="btn btn-outline-secondary"
				title="Refresh status"
				:disabled="loading"
				@click="loadStatus"
			>
				<i class="bi bi-arrow-clockwise" :class="{ 'fa-spin': loading }"></i>
			</button>
		</div>

		<!-- FieldSeeker GIS Integration -->
		<div class="card mb-4 integration-card fieldseeker">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<div>
					<h2 class="h5 mb-0">Frontier Precision's FieldSeeker GIS</h2>
				</div>
				<img
					src="https://via.placeholder.com/100x40?text=FieldSeeker"
					alt="FieldSeeker Logo"
					height="40"
				/>
			</div>
			<div class="card-body">
				<div v-if="loading" class="text-center py-3">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>
				<template v-else>
					<div v-if="error" class="alert alert-danger" role="alert">
						{{ error }}
					</div>
					<div v-if="status" class="mb-3">
						<div v-if="status.error" class="alert alert-warning" role="alert">
							<div class="fw-semibold">
								<i class="bi bi-exclamation-triangle me-1"></i>
								FieldSeeker synchronization is failing and needs to be
								re-established.
							</div>
							<div class="mt-1">
								Reconnect FieldSeeker below to refresh the ArcGIS authorization.
								The sync will resume once the connection is restored.
							</div>
							<code class="d-block small mt-2 text-break">
								{{ status.error }}
							</code>
						</div>
						<span v-if="status.connected" class="badge text-bg-success">
							<i class="bi bi-check-circle me-1"></i>Connected
						</span>
						<span
							v-else-if="status.needsReconnect"
							class="badge text-bg-warning"
						>
							<i class="bi bi-exclamation-triangle me-1"></i>Reconnection
							required
						</span>
						<span v-else class="badge text-bg-secondary"> Not integrated </span>
						<span v-if="status.syncing" class="ms-2 text-primary">
							<i class="bi bi-arrow-repeat me-1 syncing"></i>Syncing now…
						</span>
						<p class="text-muted mb-0 mt-2">
							Last synced
							<template v-if="status.lastSync">
								<TimeRelativeWithTooltip :time="status.lastSync" />.
							</template>
							<template v-else>never.</template>
						</p>
					</div>
					<p v-else class="text-muted mb-3">Status unavailable.</p>
					<div class="d-flex gap-2 mb-4">
						<a href="/oauth/arcgis/begin">
							<button class="btn btn-primary">
								<i class="bi bi-plug me-2"></i>Connect FieldSeeker
							</button>
						</a>
						<button
							v-if="status && status.connected"
							type="button"
							class="btn btn-outline-danger"
							:disabled="disconnecting"
							@click="disconnect"
						>
							<i class="bi bi-x-circle me-2"></i>Remove integration
						</button>
					</div>
					<div v-if="status && status.attempts.length" class="mt-2">
						<div v-if="status.runtime" class="text-muted small mb-2">
							<strong>{{ status.runtime.count }}</strong>
							successful sync
							{{ status.runtime.count === 1 ? "run" : "runs" }} · fastest
							{{ formatDurationMs(status.runtime.minSeconds * 1000) }} · median
							{{ formatDurationMs(status.runtime.medianSeconds * 1000) }} ·
							average
							{{ formatDurationMs(status.runtime.averageSeconds * 1000) }} ·
							slowest {{ formatDurationMs(status.runtime.maxSeconds * 1000) }}
						</div>
						<h3 class="h6 fw-semibold mb-2">Recent syncs</h3>
						<div class="table-responsive">
							<table class="table table-sm align-middle mb-0">
								<thead>
									<tr>
										<th scope="col">Started</th>
										<th scope="col">Runtime</th>
										<th scope="col">Result</th>
										<th scope="col">Created</th>
										<th scope="col">Updated</th>
										<th scope="col">Unchanged</th>
										<th scope="col">Error</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="attempt in status.attempts" :key="attempt.id">
										<td>
											<TimeRelativeWithTooltip
												v-if="attempt.startedAt"
												:time="attempt.startedAt"
											/>
											<span v-else>—</span>
										</td>
										<td>
											<span v-if="attempt.completedAt">
												{{ formatDurationMs(runtimeMs(attempt)) }}
											</span>
											<span
												v-else-if="attempt.success === null"
												class="text-muted"
												>running</span
											>
											<span v-else class="text-muted">—</span>
										</td>
										<td>
											<span
												v-if="attempt.success === null"
												class="text-primary"
											>
												<i class="bi bi-arrow-repeat me-1 syncing"></i>Running
											</span>
											<span v-else-if="attempt.success" class="text-success">
												<i class="bi bi-check-circle me-1"></i>Succeeded
											</span>
											<span v-else class="text-danger">
												<i class="bi bi-x-circle me-1"></i>Failed
											</span>
										</td>
										<td>{{ attempt.recordsCreated }}</td>
										<td>{{ attempt.recordsUpdated }}</td>
										<td>{{ attempt.recordsUnchanged }}</td>
										<td>
											<code v-if="attempt.error" class="small text-break">
												{{ attempt.error }}
											</code>
											<span v-else class="text-muted">—</span>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</template>
			</div>
		</div>

		<FieldseekerTechnicianNames />
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import FieldseekerTechnicianNames from "@/components/FieldseekerTechnicianNames.vue";
import { apiClient } from "@/client";

interface FieldseekerSyncAttempt {
	id: number;
	startedAt: string;
	completedAt: string | null;
	success: boolean | null; // null while the attempt is running
	error: string;
	recordsCreated: number;
	recordsUpdated: number;
	recordsUnchanged: number;
	accountId: string;
	oauthTokenId: number | null;
}

interface FieldseekerSyncRuntime {
	count: number;
	minSeconds: number;
	medianSeconds: number;
	averageSeconds: number;
	maxSeconds: number;
}

interface FieldseekerStatus {
	connected: boolean;
	needsReconnect: boolean;
	syncing: boolean;
	lastSync: string | null;
	error: string;
	attempts: FieldseekerSyncAttempt[];
	runtime: FieldseekerSyncRuntime | null;
}

const status = ref<FieldseekerStatus | null>(null);
const loading = ref(true);
const error = ref("");
const disconnecting = ref(false);

// runtimeMs returns an attempt's elapsed runtime in milliseconds (completed -
// started), or null while it is still running.
function runtimeMs(attempt: FieldseekerSyncAttempt): number | null {
	if (!attempt.completedAt || !attempt.startedAt) return null;
	return (
		new Date(attempt.completedAt).getTime() -
		new Date(attempt.startedAt).getTime()
	);
}

// formatDurationMs renders a duration in milliseconds as a compact, human
// readable span (e.g. "45s", "2m 13s", "1h 5m").
function formatDurationMs(ms: number | null | undefined): string {
	if (ms == null || !isFinite(ms) || ms < 0) return "—";
	const totalSeconds = ms / 1000;
	if (totalSeconds < 60) {
		// Keep sub-second precision only for very short runs (e.g. an oauth
		// failure that aborts instantly).
		const s =
			totalSeconds < 10
				? Math.round(totalSeconds * 10) / 10
				: Math.round(totalSeconds);
		return `${s}s`;
	}
	const totalMinutes = Math.floor(totalSeconds / 60);
	const seconds = Math.floor(totalSeconds % 60);
	if (totalMinutes < 60) return `${totalMinutes}m ${seconds}s`;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	if (minutes === 0) return `${hours}h`;
	return `${hours}h ${minutes}m`;
}

async function loadStatus() {
	loading.value = true;
	error.value = "";
	try {
		status.value = await apiClient.JSONGet<FieldseekerStatus>(
			"/api/integration/fieldseeker",
		);
	} catch (e: any) {
		error.value =
			e?.message || "Failed to load FieldSeeker integration status.";
	} finally {
		loading.value = false;
	}
}

async function disconnect() {
	if (
		!window.confirm(
			"Remove this FieldSeeker integration? This revokes the ArcGIS " +
				"authorization, stops scheduled syncs, and clears the org's " +
				"connection so you can reconnect with a different ArcGIS account.",
		)
	) {
		return;
	}
	disconnecting.value = true;
	error.value = "";
	try {
		await apiClient.JSONPost("/api/integration/fieldseeker/disconnect", {});
		await loadStatus();
	} catch (e: any) {
		error.value = e?.message || "Failed to remove the FieldSeeker integration.";
	} finally {
		disconnecting.value = false;
	}
}

onMounted(loadStatus);
</script>

<style scoped>
.integration-card {
	border-left: 5px solid #0d6efd;
}

.syncing {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}
</style>
